import React, { useEffect, useState } from 'react';
import { Button, Container, Table, Image, Badge, Pagination, Form, Row, Col, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BlogManagement.css';

const BlogManagement = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [newsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [approvingNewsId, setApprovingNewsId] = useState(null);
    const [deletingNewsId, setDeletingNewsId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        try {
            const response = await axios.get('https://api.admin-panel.nhcholding.com/api/news', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data && Array.isArray(response.data.data)) {
                setNews(response.data.data);
            } else {
                toast.error('Failed to load news.');
            }
        } catch (error) {
            console.error('Error fetching news:', error);
            toast.error('Error loading news.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved & Published', variant: 'success' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            manager_approved: { text: 'Approved by Manager', variant: 'info' },
            pending: { text: 'In Review', variant: 'secondary' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'dark' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    const handleAddNewBlog = () => {
        navigate('/cms/blog-management/add-new');
    };

    const handleView = (id) => {
        navigate(`/cms/blog-management/view/${id}`);
    };

    const handleEdit = (id) => {
        navigate(`/cms/blog-management/edit/${id}`);
    };

    const handleDelete = async (newsId) => {
        setDeletingNewsId(newsId);
        try {
            await axios.delete(`https://api.admin-panel.nhcholding.com/api/news/${newsId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            toast.success('News has been deleted successfully');
            fetchNews(); // Refresh the list
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting news:', error);
            toast.error('Failed to delete news');
        } finally {
            setDeletingNewsId(null);
        }
    };

    const handleToggleActive = async (newsId, currentStatus) => {
        setApprovingNewsId(newsId);
        try {
            await axios.put(`https://api.admin-panel.nhcholding.com/api/news/${newsId}/toggle-active`, null, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            toast.success(`News has been ${currentStatus ? 'deactivated' : 'activated'}`);
            fetchNews(); // Refresh the list
        } catch (error) {
            console.error('Error toggling news status:', error);
            toast.error('Failed to update news status');
        } finally {
            setApprovingNewsId(null);
        }
    };

    const openDeleteModal = (newsItem) => {
        setSelectedNews(newsItem);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedNews(null);
    };

    const filteredNews = news.filter((newsItem) => {
        const matchesSearch = 
            newsItem.title_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
            newsItem.title_ar.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter ? newsItem.status === statusFilter : true;

        return matchesSearch && matchesStatus;
    });

    const indexOfLastNews = currentPage * newsPerPage;
    const indexOfFirstNews = indexOfLastNews - newsPerPage;
    const currentNews = filteredNews.slice(indexOfFirstNews, indexOfLastNews);
    const totalPages = Math.ceil(filteredNews.length / newsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) return <div>Loading news...</div>;

    return (
        <Container fluid>
            <div className="table-container">
                <ToastContainer />
                <div className="d-flex justify-content-between align-items-center mt-5 mb-5">
                    <h3>News Management</h3>
                    <Button variant="primary" onClick={handleAddNewBlog}>Add New News</Button>
                </div>

                <Form className="mb-3">
                    <Row>
                        <Col md={6}>
                            <Form.Control
                                type="search"
                                placeholder="Search by News Title"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-brand-shadow"
                            />
                        </Col>
                        <Col md={4}>
                            <Form.Control
                                as="select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="input-brand-shadow"
                            >
                                <option value="">All Status</option>
                                <option value="approved">Approved & Published</option>
                                <option value="rejected_for_edit">Needs Revision</option>
                                <option value="rejected">Rejected</option>
                                <option value="manager_approved">Approved by Manager</option>
                                <option value="pending">In Review</option>
                            </Form.Control>
                        </Col>
                        <Col md={2} className="text-end">
                            <Button variant="secondary" onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('');
                            }}>Clear Filters</Button>
                        </Col>
                    </Row>
                </Form>

                <div className="table-brand-shadow">
                    <Table responsive striped bordered hover className="text-center">
                        <thead>
                            <tr>
                                <th>Sr. No</th>
                                <th>News Id</th>
                                <th>Featured Image</th>
                                <th>Title (En)</th>
                                <th>Title (Ar)</th>
                                <th>Created By</th>
                                <th>Status</th>
                                <th>Active</th>
                                <th>Created Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentNews.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="text-center">No news available</td>
                                </tr>
                            ) : (
                                currentNews.map((newsItem, index) => (
                                    <tr key={newsItem.id}>
                                        <td>{indexOfFirstNews + index + 1}</td>
                                        <td>{newsItem.id}</td>
                                        <td>
                                            {newsItem.featured_image && (
                                                <Image
                                                    src={`https://api.admin-panel.nhcholding.com/storage/${newsItem.featured_image}`}
                                                    alt="Featured Image"
                                                    width={80}
                                                    height={80}
                                                    rounded
                                                />
                                            )}
                                        </td>
                                        <td>{newsItem.title_en}</td>
                                        <td>{newsItem.title_ar}</td>
                                        <td>{newsItem.creator?.name || 'Unknown'}</td>
                                        <td>{getStatusBadge(newsItem.status)}</td>
                                        <td>
                                            <Badge bg={newsItem.is_active ? 'success' : 'danger'}>
                                                {newsItem.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>{new Date(newsItem.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {/* <Button variant="info" size="sm" onClick={() => handleView(newsItem.id)}>View</Button>{' '} */}
                                            <Button variant="warning" size="sm" onClick={() => handleEdit(newsItem.id)}>Edit</Button>{' '}
                                            {newsItem.status === 'approved' && (
                                                <Button
                                                    variant={newsItem.is_active ? 'secondary' : 'success'}
                                                    size="sm"
                                                    onClick={() => handleToggleActive(newsItem.id, newsItem.is_active)}
                                                    disabled={approvingNewsId === newsItem.id}
                                                >
                                                    {approvingNewsId === newsItem.id ? (
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    ) : (
                                                        newsItem.is_active ? 'Deactivate' : 'Activate'
                                                    )}
                                                </Button>
                                            )}{' '}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>

                <Pagination className="justify-content-end">
                    {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>

            </div>
        </Container>
    );
};

export default BlogManagement;
